import moment from 'moment';
import { range } from './arrays';
import 'moment/locale/fi';
import type { ProductColorFull, ValidShipDates } from '@/server/assortments/assortment-products';

export function getAssortmentAddDialogSeasons() {
  const currentYear = moment().year();
  const currentMonth = moment().month();
  const years = range(currentYear - 1, currentYear + 5, 1);
  return years
    .reduce<string[]>(
      (acc, year) => [
        ...acc,
        ...(currentMonth <= 6 ? [`KK/${year}`, `ST/${year}`] : [`ST/${year}`, `KK/${year + 1}`]),
      ],
      []
    )
    .map((season) => ({
      value: season,
      name: season,
    }));
}

export function getAssortmentListSeasons() {
  const currentYear = moment().year();
  const years = range(currentYear - 5, currentYear + 5, 1).sort((a, b) => -(a - b));
  return years
    .reduce<string[]>((acc, year) => [...acc, ...[`KK/${year}`, `ST/${year}`]], [])
    .map((season) => ({
      value: season,
      name: season,
    }));
}

export function getAllSeasons() {
  const currentYear = moment().year();
  const startYear = 2024; // Assuming seasons start from year 2024
  const years = range(startYear, currentYear + 5, 1).sort((a, b) => -(a - b));
  return years
    .reduce<string[]>((acc, year) => [...acc, ...[`KK/${year}`, `ST/${year}`]], [])
    .map((season) => ({
      value: season,
      name: season,
    }));
}

export function getInitialQuarter(
  initialSeason: string,
  quartersMapped: Record<string, Record<string, { value: string; name: string }>> = {}
) {
  if (!initialSeason) return { initialQuarter: 'Q1' };

  // Try finding the next season that is probably in the works at the moment
  // That would be 2 quarters ahead of the current season
  const twoQuartersFromNow = new Date(Date.now() + (365 / 2) * 24 * 60 * 60 * 1000);

  // Get the right quarter
  const initialSeasonShort = initialSeason.split('/')[0];
  const quarterOptionsMapped = initialSeasonShort ? quartersMapped[initialSeasonShort] || {} : {};
  const wantedQuarter = `Q${Math.ceil((twoQuartersFromNow.getMonth() + 1) / 3)}`;
  const initialQuarter =
    Object.keys(quarterOptionsMapped).find((quarter) => quarter === wantedQuarter) ||
    Object.keys(quarterOptionsMapped)[0];

  return { initialQuarter };
}

type AnyObjectWithColorValidShipDates = Pick<ProductColorFull, 'colorValidShipDates'> &
  Record<string, unknown>;

type ValidatedShipDates = {
  from: Date;
  to: Date | null;
};
/**
 * Returns an range of delivery dates that are valid for all selected products.
 *
 * @param {Object|Object[]} productColors An array of objects containing product colors and their valid ship dates, like this: { colorValidShipDates: { from: '2023-06-01', to: '2023-06-30' } }
 * You may also provide a single object instead of an array. In that case, the object must only contain properties from and to.
 * @returns {Object{from,to}} An object containing valid delivery dates from - to that are valid for all selected products
 */
export function getAvailableDeliveryDatesByColorsValidShipDates(
  productColors: AnyObjectWithColorValidShipDates[] | ValidShipDates
): ValidatedShipDates {
  const now = new Date();
  const validShipDates: ValidatedShipDates = {
    from: now,
    to: null,
  };

  if (typeof productColors === 'object' && 'to' in productColors && 'from' in productColors) {
    productColors = [{ colorValidShipDates: productColors }];
  }

  if (!Array.isArray(productColors)) {
    return validShipDates;
  }

  // Select ship dates that are valid for all selected products: max(color.shipdatefrom) & min(color.shipdateto)
  const validShipDateFromTimestamp = Math.max(
    ...productColors.map((color) => {
      if (!color?.colorValidShipDates?.from) return 0;
      return new Date(color?.colorValidShipDates?.from).getTime();
    })
  );

  const validShipDateToTimestamp = Math.min(
    ...productColors.map((color) => {
      if (!color?.colorValidShipDates?.to) return Infinity;
      return new Date(color?.colorValidShipDates?.to).getTime();
    })
  );

  validShipDates.from = validShipDateFromTimestamp > 0 ? new Date(validShipDateFromTimestamp) : now;
  validShipDates.to =
    validShipDateToTimestamp < Infinity ? new Date(validShipDateToTimestamp) : null;

  // If from > to, there are no valid ship dates. This is probably an error, so allow all ship dates.
  if (!validShipDates.to || validShipDates.from > validShipDates.to) {
    validShipDates.from = now;
    validShipDates.to = null;
  }

  return validShipDates;
}

export function updateAvailableDeliveryDatesForMethod(
  availableDeliveryDates: ValidatedShipDates,
  deliveryMethod: string,
  purchaseType: string
) {
  // If "Keskusvarasto" & "Yhteisostotilaus", move first available delivery date by one week
  if (deliveryMethod === 'ZNOR' && purchaseType === 'SHOP') {
    const fromDate = new Date(availableDeliveryDates.from);
    fromDate.setDate(fromDate.getDate() + 7);

    availableDeliveryDates.from = fromDate;
  }

  return availableDeliveryDates;
}

export function getCurrentSeason() {
  const year = moment().year();
  const month = moment().month();
  return month < 6 ? `KK/${year}` : `ST/${year}`;
}

export function getPreviousSeason(season: string) {
  const [seasonCode, yearPart] = season.split('/');

  if (
    !seasonCode ||
    !yearPart ||
    !['KK', 'ST'].includes(seasonCode) ||
    isNaN(Number.parseInt(yearPart)) ||
    Number.parseInt(yearPart) < 2021
  ) {
    throw new Error(`Invalid season format: ${season}`);
  }

  if (seasonCode == 'ST') {
    return `KK/${yearPart}`;
  }

  const previousYear = Number.parseInt(yearPart) - 1;
  return `ST/${previousYear}`;
}

function getNextSeason(season: string) {
  const [seasonCode, yearPart] = season.split('/');

  if (
    !seasonCode ||
    !yearPart ||
    !['KK', 'ST'].includes(seasonCode) ||
    isNaN(Number.parseInt(yearPart)) ||
    Number.parseInt(yearPart) < 2021
  ) {
    throw new Error(`Invalid season format: ${season}`);
  }

  if (seasonCode == 'KK') {
    return `ST/${yearPart}`;
  }

  const nextYear = Number.parseInt(yearPart) + 1;
  return `KK/${nextYear}`;
}

export function getNextSeasons(seasonCount: number, season: string | null | undefined = null) {
  let currentSeason = season ? season : getCurrentSeason();

  const seasons = [];
  for (let i = 0; i < seasonCount; i++) {
    currentSeason = getNextSeason(currentSeason);
    seasons.push(currentSeason);
  }

  return seasons;
}

export function firstSeasonIsEarlierOrSameThanLast(firstSeason: string, lastSeason: string) {
  const [firstSeasonCode, firstYearPart] = firstSeason.split('/');
  const [lastSeasonCode, lastYearPart] = String(lastSeason).split('/');

  if (firstYearPart && lastYearPart) {
    if (Number.parseInt(lastYearPart) < Number.parseInt(firstYearPart)) {
      // Last season year is earlier than first season year
      return false;
    }

    // If the years are the same
    if (firstYearPart === lastYearPart) {
      // If last season code is before first season code, return false
      if (lastSeasonCode === 'KK' && firstSeasonCode === 'ST') {
        return false;
      }
    }
  }

  // If season format is invalid, assume it's earlier than last season to avoid issues
  // If the first season year is earlier than the last season year, return true
  // If the years are the same and the first season code is before the last season code or the same, return true
  return true;
}
